import React, { useState, useEffect, useRef, useMemo } from 'react';
import { api as axios } from '../utils/apiClient';
import { isUpdateAvailable, hasWordPressReportedUpdate } from '../utils/pluginUtils';
import { useUpdatePlugin } from '../hooks/useUpdatePlugin';
import ReasonModal from './ReasonModal';
import './SiteDetails.css';

const buildEndpointCandidates = (siteUrl) => {
  try {
    const parsed = new URL(siteUrl);
    const bases = new Set();
    const cleanPath = parsed.pathname.replace(/\/+$/, '');
    bases.add(`${parsed.origin}${cleanPath}`);
    bases.add(parsed.origin);

    const segments = parsed.pathname.split('/').filter(Boolean);
    if (segments.length > 0) {
      let cumulative = '';
      segments.forEach((segment) => {
        cumulative += `/${segment}`;
        bases.add(`${parsed.origin}${cumulative}`);
      });
    }

    return Array.from(bases).map((base) => base.replace(/\/+$/, ''));
  } catch (error) {
    console.warn('Failed to parse site URL for endpoint candidates:', error);
    return [siteUrl.replace(/\/+$/, '')];
  }
};

const Plugins = ({ site }) => {
  const [pluginData, setPluginData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  // Get updatePlugin function from hook - pass siteId to hook
  const { updatePlugin, isUpdatingTarget, error: updateError, success: updateSuccess, lastResult } = useUpdatePlugin(site?._id);
  const [toast, setToast] = useState(null);
  const [activatingPlugins, setActivatingPlugins] = useState(new Set());
  const [deactivatingPlugins, setDeactivatingPlugins] = useState(new Set());
  const [autoUpdateSettings, setAutoUpdateSettings] = useState({});
  const [updatingAutoUpdate, setUpdatingAutoUpdate] = useState(new Set());
  const isDisablingAutoUpdateRef = useRef(false); // Prevent event handlers from interfering during disable
  const [reasonModal, setReasonModal] = useState({
    isOpen: false,
    action: null, // 'activate', 'deactivate', 'update', 'enable-auto-update'
    pluginSlug: null,
    pluginFile: null,
    pluginName: null,
    packageUrl: null,
  });
  
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };
  
  // Activate plugin function (internal - called after reason is provided)
  const activatePluginInternal = async (pluginSlug, pluginFile, reason) => {
    if (!site?._id) {
      showToast('Site ID is missing', 'error');
      return { success: false, error: 'Site ID missing' };
    }
    
    const pluginKey = pluginSlug || pluginFile || 'unknown';
    if (activatingPlugins.has(pluginKey)) {
      return { success: false, error: 'Activation already in progress' };
    }
    
    setActivatingPlugins(prev => new Set(prev).add(pluginKey));
    
    try {
      const response = await axios.post(`/api/sites/${site._id}/activate-plugin`, {
        slug: pluginSlug,
        plugin_slug: pluginSlug,
        plugin_file: pluginFile,
        reason: reason || ''
      });
      
      if (response.data?.success) {
        showToast(response.data.message || 'Plugin activated successfully', 'success');
        // Refresh plugin list after successful activation
        setTimeout(async () => {
          await fetchPlugins(false, true);
        }, 500);
        return { success: true, data: response.data };
      } else {
        showToast(response.data?.message || 'Failed to activate plugin', 'error');
        return { success: false, error: response.data?.message || 'Activation failed' };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to activate plugin';
      showToast(errorMessage, 'error');
      return { success: false, error: errorMessage };
    } finally {
      setActivatingPlugins(prev => {
        const next = new Set(prev);
        next.delete(pluginKey);
        return next;
      });
    }
  };

  // Activate plugin function (shows modal first)
  const activatePlugin = (pluginSlug, pluginFile, pluginName) => {
    const plugin = pluginData?.plugins?.find(p => 
      (p.slug || p.plugin) === pluginSlug || p.plugin === pluginFile
    );
    const name = pluginName || plugin?.name || plugin?.Name || pluginSlug || 'Unknown Plugin';
    
    setReasonModal({
      isOpen: true,
      action: 'activate',
      pluginSlug,
      pluginFile,
      pluginName: name,
      packageUrl: null,
    });
  };
  
  // Deactivate plugin function (internal - called after reason is provided)
  const deactivatePluginInternal = async (pluginSlug, pluginFile, reason) => {
    if (!site?._id) {
      showToast('Site ID is missing', 'error');
      return { success: false, error: 'Site ID missing' };
    }
    
    const pluginKey = pluginSlug || pluginFile || 'unknown';
    if (deactivatingPlugins.has(pluginKey)) {
      return { success: false, error: 'Deactivation already in progress' };
    }
    
    setDeactivatingPlugins(prev => new Set(prev).add(pluginKey));
    
    try {
      const response = await axios.post(`/api/sites/${site._id}/deactivate-plugin`, {
        slug: pluginSlug,
        plugin_slug: pluginSlug,
        plugin_file: pluginFile,
        reason: reason || ''
      });
      
      if (response.data?.success) {
        showToast(response.data.message || 'Plugin deactivated successfully', 'success');
        // Refresh plugin list after successful deactivation
        setTimeout(async () => {
          await fetchPlugins(false, true);
        }, 500);
        return { success: true, data: response.data };
      } else {
        showToast(response.data?.message || 'Failed to deactivate plugin', 'error');
        return { success: false, error: response.data?.message || 'Deactivation failed' };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to deactivate plugin';
      showToast(errorMessage, 'error');
      return { success: false, error: errorMessage };
    } finally {
      setDeactivatingPlugins(prev => {
        const next = new Set(prev);
        next.delete(pluginKey);
        return next;
      });
    }
  };

  // Deactivate plugin function (shows modal first)
  const deactivatePlugin = (pluginSlug, pluginFile, pluginName) => {
    const plugin = pluginData?.plugins?.find(p => 
      (p.slug || p.plugin) === pluginSlug || p.plugin === pluginFile
    );
    const name = pluginName || plugin?.name || plugin?.Name || pluginSlug || 'Unknown Plugin';
    
    setReasonModal({
      isOpen: true,
      action: 'deactivate',
      pluginSlug,
      pluginFile,
      pluginName: name,
      packageUrl: null,
    });
  };

  // Toggle auto-update for a plugin
  const toggleAutoUpdate = (pluginSlug, pluginName) => {
    if (!site?._id) {
      showToast('Site ID is missing', 'error');
      return;
    }

    // Normalize slug to match backend format (use helper function)
    const slug = normalizeSlug(pluginSlug);
    if (!slug) {
      showToast('Unable to determine plugin slug', 'error');
      return;
    }
    
    console.log(`[Plugins] Toggle auto-update for:`, { originalSlug: pluginSlug, normalizedSlug: slug });
    
    // Check if enabled - must be explicitly true, not just truthy
    const isEnabled = autoUpdateSettings[slug] === true;
    
    // If disabling, just disable without popup
    if (isEnabled) {
      handleDisableAutoUpdate(slug, pluginName);
      return;
    }

    // If enabling, show reason popup first
    setReasonModal({
      isOpen: true,
      action: 'enable-auto-update',
      pluginSlug: slug,
      pluginFile: null,
      pluginName: pluginName || slug,
      packageUrl: null,
    });
  };

  // Handle enabling auto-update with reason
  const handleEnableAutoUpdate = async (slug, pluginName, reason) => {
    setUpdatingAutoUpdate(prev => new Set(prev).add(slug));

    try {
      // Save auto-update setting with reason
      const response = await axios.put(`/api/sites/${site._id}/plugin-settings`, {
        slug: slug,
        auto_update: true,
        plugin_name: pluginName || slug, // Pass plugin name to backend for logging
        meta: {
          auto_update_reason: reason || 'Auto-update enabled'
        }
      });

      if (response.data?.success) {
        // Update state first so the interval effect can pick it up
        // Ensure slug is normalized
        const normalizedSlug = normalizeSlug(slug);
        if (normalizedSlug) {
          setAutoUpdateSettings(prev => ({
            ...prev,
            [normalizedSlug]: true
          }));
        }
        showToast(
          `Auto-update enabled for ${pluginName || slug}`,
          'success'
        );
        
        // Dispatch event to refresh plugin logs (delay to ensure backend has saved the log)
        setTimeout(() => {
          console.log('[Plugins] Dispatching auto-update-changed event (enabled)');
          window.dispatchEvent(new CustomEvent('auto-update-changed', {
            detail: { action: 'enabled', slug: normalizedSlug, pluginName }
          }));
        }, 1500);
        
        // Immediately check for updates when auto-update is enabled
        // Wait a bit longer to ensure database is updated, then trigger immediate update
        console.log(`[Plugins] Auto-update enabled for ${slug}, will trigger immediate update check...`);
        setTimeout(async () => {
          try {
            // First, check if this specific plugin has an update available
            const currentPlugins = pluginData?.plugins || [];
            const currentPlugin = currentPlugins.find(p => {
              const pluginSlug = getPluginSlug(p);
              return pluginSlug && pluginSlug.toLowerCase() === slug.toLowerCase();
            });
            
            if (currentPlugin) {
              const hasUpdate = hasWordPressReportedUpdate(currentPlugin);
              console.log(`[Plugins] Plugin ${slug} update check:`, {
                hasUpdate,
                currentVersion: currentPlugin.version,
                newVersion: currentPlugin.update?.new_version,
                updateObject: currentPlugin.update
              });
              
              // If update is available, update immediately
              if (hasUpdate && currentPlugin.update?.new_version) {
                const pluginSlug = getPluginSlug(currentPlugin);
                const packageUrl = currentPlugin.update?.package || 
                                 currentPlugin.update?.download_link || 
                                 currentPlugin.update?.package_url;
                const pluginFile = currentPlugin.plugin || currentPlugin.plugin_file;
                const pluginName = currentPlugin.name || currentPlugin.Name || slug;
                
                console.log(`[Plugins] ⚡ Update available for ${pluginName}, updating immediately...`);
                showToast(
                  `Auto-update enabled. Updating ${pluginName} to ${currentPlugin.update.new_version}...`,
                  'info'
                );
                
                // Update the plugin immediately
                // IMPORTANT: Append "(Auto-update)" to reason so it can be identified in logs
                const autoUpdateReason = reason 
                  ? `${reason} (Auto-update)` 
                  : 'Auto-update enabled - immediate update';
                updatePlugin(pluginSlug, { 
                  packageUrl, 
                  pluginFile, 
                  reason: autoUpdateReason
                })
                  .then((result) => {
                    console.log('[Plugins] Immediate update result:', result);
                    if (result?.success) {
                      showToast(
                        `Auto-update enabled: ${pluginName} updated to ${currentPlugin.update.new_version}!`,
                        'success'
                      );
                      // Refresh plugin list after update
                      setTimeout(async () => {
                        await fetchPlugins(false, true);
                        // CRITICAL: Reload auto-update settings to preserve checkbox state
                        if (site?._id) {
                          try {
                            const response = await axios.get(`/api/sites/${site._id}`);
                            if (response.data?.pluginSettings) {
                              const settings = {};
                              Object.keys(response.data.pluginSettings).forEach(slug => {
                                if (response.data.pluginSettings[slug]?.auto_update) {
                                  settings[slug] = true;
                                }
                              });
                              setAutoUpdateSettings(settings);
                              console.log('[Plugins] Auto-update settings reloaded after immediate update:', settings);
                            }
                          } catch (error) {
                            console.error('[Plugins] Failed to reload auto-update settings after immediate update:', error);
                          }
                        }
                      }, 1500);
                    } else {
                      console.error('[Plugins] Immediate update failed:', result?.error);
                      showToast(
                        `Auto-update enabled, but update failed: ${result?.error || 'Unknown error'}`,
                        'error'
                      );
                    }
                  })
                  .catch((error) => {
                    console.error('[Plugins] Immediate update exception:', error);
                    showToast(
                      `Auto-update enabled, but update failed: ${error.message || 'Unknown error'}`,
                      'error'
                    );
                  });
                return; // Don't run the general check, we already updated
              }
            }
            
            // If no immediate update needed, run general check
            const checkResponse = await axios.post(`/api/sites/${site._id}/auto-update-plugins`, {
              reason: reason || 'Auto-update enabled'
            });
            console.log('[Plugins] Auto-update check result:', checkResponse.data);
            if (checkResponse.data?.success) {
              const { checked, updated, failed, skipped } = checkResponse.data;
              if (updated > 0) {
                showToast(
                  `Auto-update enabled: ${updated} plugin(s) updated immediately!`,
                  'success'
                );
                // Refresh plugin list after update
                setTimeout(async () => {
                  await fetchPlugins(false, true);
                  // CRITICAL: Reload auto-update settings to preserve checkbox state
                  if (site?._id) {
                    try {
                      const response = await axios.get(`/api/sites/${site._id}`);
                      if (response.data?.pluginSettings) {
                        const settings = {};
                        Object.keys(response.data.pluginSettings).forEach(slug => {
                          if (response.data.pluginSettings[slug]?.auto_update) {
                            settings[slug] = true;
                          }
                        });
                        setAutoUpdateSettings(settings);
                        console.log('[Plugins] Auto-update settings reloaded after auto-update check:', settings);
                      }
                    } catch (error) {
                      console.error('[Plugins] Failed to reload auto-update settings after auto-update check:', error);
                    }
                  }
                }, 1500);
              } else if (checked > 0) {
                console.log(`[Plugins] Auto-update check: ${checked} checked, ${skipped} skipped, no updates available`);
                showToast(
                  `Auto-update enabled. Will check for updates every 5 minutes automatically.`,
                  'info'
                );
              } else {
                console.log(`[Plugins] Auto-update check: ${skipped} skipped (likely slug mismatch)`);
                showToast(
                  `Auto-update enabled. Will check for updates every 5 minutes automatically.`,
                  'info'
                );
              }
            }
          } catch (err) {
            console.error('[Plugins] Error checking for updates after enabling auto-update:', err);
            // Don't show error toast - the enable was successful, it will retry automatically
          }
        }, 1000); // Increased delay to ensure database is updated
      } else {
        showToast(response.data?.message || 'Failed to enable auto-update', 'error');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to enable auto-update';
      showToast(errorMessage, 'error');
    } finally {
      setUpdatingAutoUpdate(prev => {
        const next = new Set(prev);
        next.delete(slug);
        return next;
      });
    }
  };

  // Handle disabling auto-update
  const handleDisableAutoUpdate = async (slug, pluginName) => {
    const normalizedSlug = normalizeSlug(slug);
    if (!normalizedSlug) {
      showToast('Unable to determine plugin slug', 'error');
      return;
    }
    
    // Set flag to prevent event handlers from interfering
    isDisablingAutoUpdateRef.current = true;
    setUpdatingAutoUpdate(prev => new Set(prev).add(normalizedSlug));

    try {
      console.log(`[Plugins] Disabling auto-update for "${slug}" (normalized: "${normalizedSlug}")`);
      
      const response = await axios.put(`/api/sites/${site._id}/plugin-settings`, {
        slug: slug,
        auto_update: false,
        plugin_name: pluginName || slug // Pass plugin name to backend for logging
      });

      console.log(`[Plugins] Disable response:`, {
        success: response.data?.success,
        settings: response.data?.settings,
        auto_update: response.data?.settings?.auto_update
      });

      if (response.data?.success) {
        // CRITICAL: Verify the response shows auto_update as false
        const responseAutoUpdate = response.data?.settings?.auto_update;
        console.log(`[Plugins] Disable API response - settings.auto_update:`, responseAutoUpdate, `(type: ${typeof responseAutoUpdate})`);
        
        if (responseAutoUpdate !== false && responseAutoUpdate !== undefined) {
          console.warn(`[Plugins] ⚠️ WARNING: Backend returned auto_update as ${responseAutoUpdate} instead of false!`);
        }
        
        // CRITICAL: Remove from state immediately to reflect disabled state
        setAutoUpdateSettings(prev => {
          const next = { ...prev };
          delete next[normalizedSlug];
          console.log(`[Plugins] ✅ Removed "${normalizedSlug}" from auto-update settings. Remaining enabled:`, Object.keys(next));
          return next;
        });
        
        // CRITICAL: Reload settings from backend after a delay to ensure consistency
        // Use a longer delay to ensure backend has fully saved and any event handlers have completed
        setTimeout(async () => {
          try {
            console.log(`[Plugins] Starting verification for "${normalizedSlug}" after disable...`);
            const verifyResponse = await axios.get(`/api/sites/${site._id}`);
            console.log(`[Plugins] Verifying auto-update settings after disable - Full response:`, {
              hasPluginSettings: !!verifyResponse.data?.pluginSettings,
              pluginSettingsKeys: verifyResponse.data?.pluginSettings ? Object.keys(verifyResponse.data.pluginSettings) : [],
              pluginSettingsValues: verifyResponse.data?.pluginSettings ? Object.entries(verifyResponse.data.pluginSettings).map(([k, v]) => ({
                slug: k,
                auto_update: v?.auto_update,
                auto_updateType: typeof v?.auto_update
              })) : []
            });
            
            if (verifyResponse.data?.pluginSettings) {
              const settings = {};
              let foundDisabledSlug = false;
              
              Object.keys(verifyResponse.data.pluginSettings).forEach(s => {
                const normSlug = normalizeSlug(s);
                const autoUpdateValue = verifyResponse.data.pluginSettings[s]?.auto_update;
                console.log(`[Plugins] Verification - slug "${s}" (normalized: "${normSlug}"): auto_update = ${autoUpdateValue} (type: ${typeof autoUpdateValue})`);
                
                if (normSlug === normalizedSlug) {
                  foundDisabledSlug = true;
                  console.log(`[Plugins] 🔍 Found disabled slug "${normalizedSlug}" in backend with auto_update = ${autoUpdateValue}`);
                }
                
                // CRITICAL: Only add if explicitly true
                if (normSlug && autoUpdateValue === true) {
                  settings[normSlug] = true;
                  console.log(`[Plugins] ✅ Verification added "${normSlug}" to enabled settings`);
                } else {
                  console.log(`[Plugins] ❌ Verification skipped "${normSlug}" - auto_update is ${autoUpdateValue} (not true)`);
                }
              });
              
              console.log(`[Plugins] Verified auto-update settings after disable:`, settings);
              console.log(`[Plugins] Expected "${normalizedSlug}" to NOT be in settings. Is it? ${settings[normalizedSlug] === true ? 'YES (ERROR!)' : 'NO (CORRECT)'}`);
              console.log(`[Plugins] Found disabled slug in backend: ${foundDisabledSlug ? 'YES' : 'NO'}`);
              
              // Only update if the disabled slug is NOT in the settings (meaning it's correctly disabled)
              if (!settings[normalizedSlug]) {
                setAutoUpdateSettings(settings);
                console.log(`[Plugins] ✅ Settings updated - "${normalizedSlug}" correctly excluded`);
              } else {
                console.error(`[Plugins] ❌ ERROR: "${normalizedSlug}" is still in settings! Not updating state to prevent UI inconsistency.`);
                // Don't update state - keep the disabled state we already set
              }
            } else {
              console.log(`[Plugins] No pluginSettings in verification response, clearing settings`);
              setAutoUpdateSettings({});
            }
          } catch (verifyError) {
            console.error('[Plugins] Failed to verify settings after disable:', verifyError);
            // On error, don't change state - keep the disabled state we already set
          }
        }, 1500); // Increased delay to 1.5 seconds to ensure backend has saved and events have processed
        
        showToast(
          `Auto-update disabled for ${pluginName || slug}`,
          'success'
        );
        
        // Dispatch event to refresh plugin logs (delay to ensure backend has saved the log)
        setTimeout(() => {
          console.log('[Plugins] Dispatching auto-update-changed event (disabled)');
          window.dispatchEvent(new CustomEvent('auto-update-changed', {
            detail: { action: 'disabled', slug: normalizedSlug, pluginName }
          }));
        }, 1500);
      } else {
        showToast(response.data?.message || 'Failed to disable auto-update', 'error');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to disable auto-update';
      console.error(`[Plugins] Error disabling auto-update for "${slug}":`, error);
      showToast(errorMessage, 'error');
    } finally {
      setUpdatingAutoUpdate(prev => {
        const next = new Set(prev);
        next.delete(normalizedSlug);
        return next;
      });
      // Clear flag after a delay to allow verification to complete
      setTimeout(() => {
        isDisablingAutoUpdateRef.current = false;
      }, 2000);
    }
  };

  // Internal function to trigger auto-update check (called automatically)
  const triggerAutoUpdateCheck = async (silent = false) => {
    if (!site?._id) {
      if (!silent) showToast('Site ID is missing', 'error');
      return;
    }

    try {
      const response = await axios.post(`/api/sites/${site._id}/auto-update-plugins`, {
        reason: 'Automatic auto-update check'
      });

      if (response.data?.success) {
        const { checked, updated, failed, skipped } = response.data;
        
        if (!silent && updated > 0) {
          showToast(
            `Auto-update: ${updated} plugin(s) updated automatically!`,
            'success'
          );
        }
        
        // Refresh plugin list after auto-update check if updates were made
        if (updated > 0) {
          setTimeout(async () => {
            await fetchPlugins(false, true);
          }, 1500);
        }
      }
    } catch (error) {
      if (!silent) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to check for updates';
        console.error('[Plugins] Auto-update check error:', errorMessage);
      }
    }
  };

  // Handle reason modal confirmation
  const handleReasonConfirm = async (reason) => {
    const { action, pluginSlug, pluginFile, packageUrl, pluginName } = reasonModal;
    
    setReasonModal({ ...reasonModal, isOpen: false });
    
    if (action === 'activate') {
      await activatePluginInternal(pluginSlug, pluginFile, reason);
    } else if (action === 'deactivate') {
      await deactivatePluginInternal(pluginSlug, pluginFile, reason);
    } else if (action === 'update') {
      const pluginFile = reasonModal.pluginFile;
      updatePlugin(pluginSlug, { packageUrl, pluginFile, reason })
        .then((result) => {
          console.log('[Plugins] Update result:', result);
          if (result?.success) {
            console.log('[Plugins] Update successful, refreshing plugin list...');
            setTimeout(async () => {
              await fetchPlugins(false, true);
            }, 500);
          } else {
            console.error('[Plugins] Update failed:', result?.error);
          }
        })
        .catch((error) => {
          console.error('[Plugins] Update exception:', error);
        });
    } else if (action === 'enable-auto-update') {
      await handleEnableAutoUpdate(pluginSlug, pluginName, reason);
    }
  };

  // Handle reason modal close
  const handleReasonClose = () => {
    setReasonModal({ ...reasonModal, isOpen: false });
  };
  
  // Handle update success/error from hook and refresh plugin list
  useEffect(() => {
    if (updateSuccess && lastResult?.success === true) {
      console.log('[Plugins] Update success detected, refreshing plugin list', lastResult);
      showToast(lastResult.message || 'Plugin updated successfully', 'success');
      // Refresh plugin list after successful update with force refresh
      // Use a longer delay to ensure WordPress has processed the update
      const refreshTimeout = setTimeout(async () => {
        console.log('[Plugins] Refreshing plugin list after successful update (force refresh)');
        setIsRefreshing(true);
        try {
          // Force refresh to get latest data without update notifications
          await fetchPlugins(false, true);
          // Wait a bit more and refresh again to ensure WordPress transient is updated
          setTimeout(async () => {
            await fetchPlugins(false, true);
          }, 1000);
        } catch (err) {
          console.error('[Plugins] Error refreshing after update', err);
        } finally {
          setIsRefreshing(false);
        }
        window.dispatchEvent(new CustomEvent('plugin-updated', { 
          detail: { 
            slug: lastResult.data?.plugin_slug,
            old_version: lastResult.old_version,
            new_version: lastResult.new_version
          } 
        }));
      }, 500);
      
      return () => clearTimeout(refreshTimeout);
    }
    if (updateError && lastResult && lastResult.success === false) {
      console.error('[Plugins] Update failed', lastResult);
      showToast(
        `Update failed: ${lastResult.error || updateError}`,
        'error'
      );
    }
  }, [updateSuccess, updateError, lastResult]);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [showTestEndpoint, setShowTestEndpoint] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testingEndpoint, setTestingEndpoint] = useState(false);
  
  // Filter and search states
  const [filter, setFilter] = useState('all'); // all, active, inactive, update_available
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlugins, setSelectedPlugins] = useState([]);
  const [bulkAction, setBulkAction] = useState('');

  const formatDate = (value) => {
    if (!value) return 'N/A';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString();
  };

  const formatCreatedDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return {
      date: parsed.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }),
      time: parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })
    };
  };

  const resolveLastUpdated = (plugin) =>
    formatDate(
      plugin?.last_updated ||
        plugin?.wporg_last_updated ||
        plugin?.UpdateDate ||
        plugin?.lastUpdated
    );

  const resolveCompatible = (plugin) =>
    plugin?.compatible_up_to ||
    plugin?.tested ||
    plugin?.update?.tested ||
    'N/A';

  const getPluginSlug = (plugin) => {
    if (!plugin) return null;
    const candidates = [
      plugin.slug,
      plugin.plugin,
      plugin.plugin_file,
      plugin.text_domain,
      plugin.name
    ];
    for (const candidate of candidates) {
      if (!candidate) continue;
      const sanitized = String(candidate).trim().toLowerCase();
      if (!sanitized || sanitized === '.') continue;
      return sanitized.split('/')[0];
    }
    return null;
  };

  const intervalRef = useRef(null);
  
  // Listen for plugin-updated events to refresh list
  useEffect(() => {
    const handlePluginUpdated = async (event) => {
      // CRITICAL: Don't reload settings if we're in the middle of disabling auto-update
      if (isDisablingAutoUpdateRef.current) {
        console.log('[Plugins] Skipping auto-update settings reload - disable operation in progress');
        return;
      }
      
      console.log('[Plugins] plugin-updated event received', event.detail);
      // Refresh plugins
      await fetchPlugins(false, true);
      // CRITICAL: Reload auto-update settings after plugin update to preserve checkbox state
      // Use the same normalization logic as fetchAutoUpdateSettings
      if (site?._id) {
        try {
          const response = await axios.get(`/api/sites/${site._id}`);
          if (response.data?.pluginSettings) {
            const settings = {};
            Object.keys(response.data.pluginSettings).forEach(slug => {
              // CRITICAL: Normalize slug to match how we check in the checkbox
              const normalizedSlug = normalizeSlug(slug);
              if (normalizedSlug && response.data.pluginSettings[slug]?.auto_update === true) {
                settings[normalizedSlug] = true;
              }
            });
            console.log('[Plugins] Auto-update settings reloaded after plugin update:', settings);
            setAutoUpdateSettings(settings);
          }
        } catch (error) {
          console.error('[Plugins] Failed to reload auto-update settings after update:', error);
        }
      }
    };
    
    window.addEventListener('plugin-updated', handlePluginUpdated);
    return () => {
      window.removeEventListener('plugin-updated', handlePluginUpdated);
    };
  }, [site?._id]);

  const fetchPlugins = async (showLoading = true, forceRefresh = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      
      const url = `/api/sites/${site._id}/plugins${forceRefresh ? '?force=true' : ''}`;
      console.log(`🔍 Fetching plugins from: ${url}`);
      const response = await axios.get(url);
      
      // Comprehensive logging of the response
      console.log('📦 Response received - Full Analysis:', {
        hasData: !!response.data,
        dataType: typeof response.data,
        isArray: Array.isArray(response.data),
        dataKeys: response.data ? Object.keys(response.data) : [],
        status: response.data?.status,
        hasPlugins: !!response.data?.plugins,
        pluginsType: typeof response.data?.plugins,
        pluginsIsArray: Array.isArray(response.data?.plugins),
        pluginsCount: response.data?.plugins?.length || 0,
        hasSummary: !!response.data?.summary,
        hasSuccess: response.data?.success !== undefined,
        successValue: response.data?.success,
        fullResponse: JSON.stringify(response.data, null, 2)
      });
      
      // Validate response.data exists
      if (!response || !response.data) {
        console.error('❌ Invalid response: response.data is missing');
        setPluginData(null);
        setError({
          status: 'error',
          errorMessage: 'Invalid response from server: missing data',
          suggestions: ['The server returned an invalid response', 'Check server logs', 'Try refreshing the page']
        });
        setLoading(false);
        return;
      }
      
      // Handle multiple response formats
      // First check: Standard format with status='ok' and plugins array
      if (response.data.status === 'ok') {
        // Ensure plugins is an array (even if empty)
        if (response.data.plugins !== undefined) {
          const pluginsArray = Array.isArray(response.data.plugins) ? response.data.plugins : [];
          // Allow empty arrays - check if it's actually an array
          if (Array.isArray(pluginsArray)) {
        // New format with status and plugins array
            // Ensure summary includes updates count if not present
            if (!response.data.summary) {
              response.data.summary = {};
            }
            if (response.data.summary.updates === undefined) {
              const updateCount = pluginsArray.filter(p => {
                if (p.hasUpdate !== undefined && p.hasUpdate !== null) return p.hasUpdate === true;
                if (p.update && typeof p.update === 'object' && (p.update.new_version || p.update.newVersion)) {
                  const newVersion = p.update.new_version || p.update.newVersion;
                  const currentVersion = p.version || p.Version || '0.0.0';
                  return newVersion !== currentVersion;
                }
                const current = p.version || p.Version;
                const latest = p.latest_version || p.latestVersion;
                return latest && current && latest !== current;
              }).length;
              response.data.summary.updates = updateCount;
            }
            // Ensure plugins is always an array
            response.data.plugins = pluginsArray;
            setPluginData(response.data);
            setLastUpdated(new Date());
            setError(null);
            console.log(`✅ Successfully loaded ${pluginsArray.length} plugins`);
            setLoading(false);
            return; // Exit early
          } else {
            // plugins exists but is not an array
            console.warn('⚠️ Plugins field is not an array:', typeof response.data.plugins);
            setPluginData(null);
            setError({
              status: 'error',
              errorMessage: 'Invalid plugins data format from server',
              suggestions: ['The server returned plugins in an unexpected format', 'Try refreshing the page', 'Check server logs for details']
            });
            setLoading(false);
            return;
          }
        } else {
          // status='ok' but no plugins field - treat as empty
          console.warn('⚠️ Status is ok but plugins field is missing, treating as empty');
          setPluginData({
            status: 'ok',
            plugins: [],
            summary: {
              total: 0,
              active: 0,
              inactive: 0,
              updates: 0
            }
          });
          setLastUpdated(new Date());
          setError(null);
          setLoading(false);
          return;
        }
      }
      
      // Second check: Format with success=true and plugins array
      if (response.data && response.data.success && response.data.plugins && Array.isArray(response.data.plugins)) {
        // Wrapped format
        setPluginData({
          status: 'ok',
          plugins: response.data.plugins,
          summary: {
            total: response.data.total || response.data.plugins.length,
            active: response.data.active_count || response.data.plugins.filter(p => p.active || p.status === 'active').length,
            inactive: response.data.inactive_count || response.data.plugins.filter(p => !p.active && p.status !== 'active').length,
            updates: response.data.update_count || 0
          }
        });
        setLastUpdated(new Date());
        setError(null);
        console.log(`✅ Successfully loaded ${response.data.plugins.length} plugins (wrapped format)`);
      } else if (Array.isArray(response.data)) {
        // Direct array format
        setPluginData({
          status: 'ok',
          plugins: response.data,
          summary: {
            total: response.data.length,
            active: response.data.filter(p => p.active || p.status === 'active').length,
            inactive: response.data.filter(p => !p.active && p.status !== 'active').length,
            updates: response.data.filter(p => p.update || p.hasUpdate).length
          }
        });
        setLastUpdated(new Date());
        setError(null);
        console.log(`✅ Successfully loaded ${response.data.length} plugins (array format)`);
      } else if (response.data && response.data.status === 'error') {
        // Error response
        setPluginData(null);
        setError(response.data);
        console.error('❌ Error response:', response.data);
      } else if (response.data && response.data.plugins && !Array.isArray(response.data.plugins)) {
        // Plugins exists but is not an array
        console.warn('⚠️ Plugins field is not an array:', typeof response.data.plugins);
        setPluginData(null);
        setError({
          status: 'error',
          errorMessage: 'Invalid plugins data format from server',
          suggestions: ['The server returned plugins in an unexpected format', 'Try refreshing the page', 'Check server logs for details']
        });
      } else {
        // Unknown format - log full response for debugging
        console.error('❌ UNKNOWN RESPONSE FORMAT - Full Response:', {
          hasData: !!response.data,
          dataKeys: response.data ? Object.keys(response.data) : [],
          dataType: typeof response.data,
          isArray: Array.isArray(response.data),
          status: response.data?.status,
          success: response.data?.success,
          hasPlugins: !!response.data?.plugins,
          pluginsType: typeof response.data?.plugins,
          pluginsIsArray: Array.isArray(response.data?.plugins),
          fullResponse: JSON.stringify(response.data, null, 2),
          responseHeaders: response.headers,
          responseStatus: response.status,
          responseStatusText: response.statusText
        });
        
        // Try to extract plugins from any possible location
        let extractedPlugins = null;
        if (Array.isArray(response.data)) {
          extractedPlugins = response.data;
        } else if (response.data?.plugins && Array.isArray(response.data.plugins)) {
          extractedPlugins = response.data.plugins;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          extractedPlugins = response.data.data;
        } else if (response.data?.items && Array.isArray(response.data.items)) {
          extractedPlugins = response.data.items;
        }
        
        if (extractedPlugins && Array.isArray(extractedPlugins)) {
          console.log('✅ Found plugins in unexpected location, attempting to use them');
          setPluginData({
            status: 'ok',
            plugins: extractedPlugins,
            summary: {
              total: extractedPlugins.length,
              active: extractedPlugins.filter(p => p.active || p.status === 'active').length,
              inactive: extractedPlugins.filter(p => !p.active && p.status !== 'active').length,
              updates: extractedPlugins.filter(p => p.update || p.hasUpdate).length
            }
          });
          setLastUpdated(new Date());
          setError(null);
          console.log(`✅ Successfully loaded ${extractedPlugins.length} plugins (extracted from unexpected format)`);
        } else {
          setPluginData(null);
          setError({
            status: 'error',
            errorMessage: 'Unexpected response format from server',
            suggestions: [
              'Check browser console for detailed response structure',
              'Check server logs for details',
              'Try refreshing the page',
              'Verify the WordPress plugin is installed and active',
              'Ensure the backend API is returning the correct format'
            ]
          });
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching plugins:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Check for specific error types
      let errorData = error.response?.data;
      
      // If no error data from response, create a structured error
      if (!errorData) {
        errorData = {
          status: 'error',
          errorMessage: error.message || 'Failed to fetch plugins',
          suggestions: [
            'Check if WordPress plugin is installed',
            'Verify Application Password credentials in site settings',
            'Ensure the site is accessible',
            'Check browser console for detailed error information'
          ]
        };
      }
      
      // Check for Authorization header errors
      if (error.message && error.message.includes('undefined') && error.message.includes('Authorization')) {
        errorData = {
          status: 'error',
          errorMessage: 'App Password not configured. The Authorization header is missing credentials.',
          suggestions: [
            'Go to Sites → Edit this website',
            'Add WordPress username and Application Password',
            'Save the site settings and refresh this page'
          ]
        };
      }
      
      setError(errorData);
      setPluginData(null);
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchPlugins(true, true);
    } catch (error) {
      console.error('Error refreshing plugins:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const callTestEndpoint = async (endpoint) => {
    try {
      const response = await axios.post(`/api/sites/${site._id}/test-endpoint`, {
        endpoint,
        authConfig: null
      });
      return { success: true, response: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        status: error.response?.status
      };
    }
  };

  const handleTestEndpoint = async () => {
    setTestingEndpoint(true);
    setTestResult(null);
    const candidateBases = buildEndpointCandidates(site.url);
    const attempts = [];
    let healthSuccess = null;
    let pluginsSuccess = null;

    try {
      for (const base of candidateBases) {
        const endpoint = `${base}/wp-json/webmonitor/v1/health`;
        const result = await callTestEndpoint(endpoint);
        attempts.push({ type: 'health', endpoint, ...result });
        if (result.success && result.response?.success) {
          healthSuccess = { endpoint, data: result.response };
          break;
        }
      }

      if (healthSuccess) {
        for (const base of candidateBases) {
          const endpoint = `${base}/wp-json/webmonitor/v1/plugins-test`;
          const result = await callTestEndpoint(endpoint);
          attempts.push({ type: 'plugins-test', endpoint, ...result });
          if (result.success && Array.isArray(result.response?.data)) {
            pluginsSuccess = { endpoint, data: result.response };
            break;
          }
        }
      }

      setTestResult({
        summary: {
          healthOk: !!healthSuccess,
          pluginsTestOk: !!pluginsSuccess,
          pluginsFound: pluginsSuccess?.data?.data?.length || 0
        },
        health: healthSuccess,
        pluginsTest: pluginsSuccess,
        attempts
      });
    } catch (error) {
      setTestResult({
        success: false,
        error: error.message
      });
    } finally {
      setTestingEndpoint(false);
    }
  };

  const updatesList = pluginData?.updatesList || [];
  const updatesMap = useMemo(() => {
    const map = new Map();
    updatesList.forEach((plugin) => {
      const key = plugin.plugin || plugin.plugin_file || plugin.slug || plugin.name;
      if (key) {
        map.set(key, plugin);
      }
    });
    return map;
  }, [updatesList]);

  const enrichedPlugins = useMemo(() => {
    if (!pluginData?.plugins) return [];
    
    return pluginData.plugins.map((plugin) => {
      const key = plugin.plugin || plugin.plugin_file || plugin.slug || plugin.name;
      const updateInfo = updatesMap.get(key);
      
      if (updateInfo) {
        return {
          ...plugin,
          hasUpdate: true,
          update: {
            new_version: updateInfo.new_version,
            current_version: updateInfo.current_version || plugin.version,
            message: updateInfo.update_message,
            tested: updateInfo.tested,
            requires: updateInfo.requires,
            requires_php: updateInfo.requires_php
          },
          latest_version: updateInfo.new_version || plugin.latest_version
        };
      }
      
      return plugin;
    });
  }, [pluginData?.plugins, updatesMap]);

  // Filter and search plugins
  const filteredPlugins = useMemo(() => {
    if (!enrichedPlugins.length) return [];
    
    let filtered = [...enrichedPlugins];
    
    // Apply filter - use hasWordPressReportedUpdate() for filtering to match backend count
    if (filter === 'active') {
      filtered = filtered.filter(p => p.active);
    } else if (filter === 'inactive') {
      filtered = filtered.filter(p => !p.active);
    } else if (filter === 'update_available') {
      // Use WordPress-only update detection for filtering to match backend count
      filtered = filtered.filter(hasWordPressReportedUpdate);
    }
    
    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) ||
        (p.description && p.description.toLowerCase().includes(query)) ||
        (p.author && p.author.toLowerCase().includes(query)) ||
        (p.slug && p.slug.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [enrichedPlugins, filter, searchQuery]);

  // Calculate filter counts - use hasWordPressReportedUpdate() to match WordPress backend exactly
  const filterCounts = useMemo(() => {
    if (!enrichedPlugins.length) return { all: 0, active: 0, inactive: 0, update_available: 0 };
    
    // Use hasWordPressReportedUpdate() to match WordPress backend count exactly
    // This only counts plugins where WordPress core reports update.new_version > current version
    const updateCount = enrichedPlugins.filter(hasWordPressReportedUpdate).length;
    
    return {
      all: enrichedPlugins.length,
      active: enrichedPlugins.filter(p => p.active).length,
      inactive: enrichedPlugins.filter(p => !p.active).length,
      update_available: updateCount
    };
  }, [enrichedPlugins]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedPlugins(filteredPlugins.map(p => p.slug || p.plugin));
    } else {
      setSelectedPlugins([]);
    }
  };

  const handleSelectPlugin = (pluginSlug) => {
    setSelectedPlugins(prev => 
      prev.includes(pluginSlug)
        ? prev.filter(s => s !== pluginSlug)
        : [...prev, pluginSlug]
    );
  };

  // Helper function to normalize slug (matches backend normalization)
  const normalizeSlug = (slug) => {
    if (!slug) return null;
    let normalized = String(slug).trim().toLowerCase();
    // Extract base slug if it contains a path
    if (normalized.includes('/')) {
      normalized = normalized.split('/')[0];
    }
    // Remove .php extension if present
    if (normalized.endsWith('.php')) {
      normalized = normalized.replace(/\.php$/, '');
    }
    return normalized;
  };

  // Fetch auto-update settings when site changes
  useEffect(() => {
    const fetchAutoUpdateSettings = async () => {
      if (!site?._id) {
        setAutoUpdateSettings({});
        return;
      }
      
      try {
        const response = await axios.get(`/api/sites/${site._id}`);
        console.log('[Plugins] Fetching auto-update settings - Full response:', {
          hasPluginSettings: !!response.data?.pluginSettings,
          pluginSettingsKeys: response.data?.pluginSettings ? Object.keys(response.data.pluginSettings) : [],
          pluginSettingsValues: response.data?.pluginSettings ? Object.entries(response.data.pluginSettings).map(([k, v]) => ({
            slug: k,
            auto_update: v?.auto_update,
            auto_updateType: typeof v?.auto_update
          })) : []
        });
        
        if (response.data?.pluginSettings) {
          const settings = {};
          Object.keys(response.data.pluginSettings).forEach(slug => {
            // Backend stores slugs normalized (lowercase, no .php, no path)
            // So we can use the key directly, but ensure it's normalized for consistency
            const normalizedSlug = normalizeSlug(slug);
            if (normalizedSlug) {
              // Only set to true if explicitly true in backend
              // If false or undefined, don't add to settings (which means disabled)
              const autoUpdateValue = response.data.pluginSettings[slug]?.auto_update;
              console.log(`[Plugins] Processing slug "${slug}" (normalized: "${normalizedSlug}"): auto_update = ${autoUpdateValue} (type: ${typeof autoUpdateValue})`);
              
              // CRITICAL: Only add to settings if explicitly true
              // If false, undefined, or any other value, don't add (means disabled)
              if (autoUpdateValue === true) {
                settings[normalizedSlug] = true;
                console.log(`[Plugins] ✅ Added "${normalizedSlug}" to enabled settings`);
              } else {
                console.log(`[Plugins] ❌ Skipping "${normalizedSlug}" - auto_update is ${autoUpdateValue} (not true)`);
              }
            }
          });
          console.log('[Plugins] Final auto-update settings loaded:', settings);
          setAutoUpdateSettings(settings);
        } else {
          // If no pluginSettings, ensure state is cleared (all plugins disabled)
          console.log('[Plugins] No pluginSettings found, clearing auto-update settings');
          setAutoUpdateSettings({});
        }
      } catch (error) {
        console.error('[Plugins] Failed to fetch auto-update settings:', error);
        // On error, clear settings to prevent showing incorrect state
        setAutoUpdateSettings({});
      }
    };
    
    fetchAutoUpdateSettings();
  }, [site?._id]);

  useEffect(() => {
    if (site && site._id) {
      fetchPlugins();
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?._id]);

  // Auto-update check interval (runs every 5 minutes for plugins with auto-update enabled)
  const autoUpdateIntervalRef = useRef(null);
  
  useEffect(() => {
    // Check if any plugins have auto-update enabled
    const hasAutoUpdateEnabled = Object.values(autoUpdateSettings).some(enabled => enabled === true);
    
    if (hasAutoUpdateEnabled && site && site._id) {
      // Clear any existing interval
      if (autoUpdateIntervalRef.current) {
        clearInterval(autoUpdateIntervalRef.current);
      }
      
      // Run initial check immediately
      console.log('[Plugins] Auto-update enabled for some plugins, running initial check...');
      triggerAutoUpdateCheck(true).catch(err => {
        console.error('[Plugins] Initial auto-update check error:', err);
      });
      
      // Then check every 5 minutes (300000 ms)
      autoUpdateIntervalRef.current = setInterval(() => {
        console.log('[Plugins] Running scheduled auto-update check...');
        triggerAutoUpdateCheck(true).catch(err => {
          console.error('[Plugins] Scheduled auto-update check error:', err);
        });
      }, 300000); // 5 minutes
      
      return () => {
        if (autoUpdateIntervalRef.current) {
          clearInterval(autoUpdateIntervalRef.current);
          autoUpdateIntervalRef.current = null;
        }
      };
    } else {
      // Clear interval if no auto-update enabled
      if (autoUpdateIntervalRef.current) {
        clearInterval(autoUpdateIntervalRef.current);
        autoUpdateIntervalRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoUpdateSettings, site?._id]);

  // Auto-refresh functionality
  useEffect(() => {
    if (autoRefresh && site && site._id && !loading) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      intervalRef.current = setInterval(async () => {
        console.log(`🔄 Auto-refreshing plugins (every ${refreshInterval}s)...`);
        await fetchPlugins(false);
      }, refreshInterval * 1000);
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshInterval, site?._id, loading]);

  const plugins = enrichedPlugins;
  const summary = pluginData?.summary || { total: 0, active: 0, inactive: 0 };
  
      // Debug logging
      useEffect(() => {
        if (pluginData) {
          // Count plugins with updates for debugging
          const pluginsWithUpdates = plugins.filter(p => {
            if (p.hasUpdate !== undefined && p.hasUpdate !== null) return p.hasUpdate === true;
            if (p.update && typeof p.update === 'object' && (p.update.new_version || p.update.newVersion)) {
              const newVersion = p.update.new_version || p.update.newVersion;
              const currentVersion = p.version || p.Version || '0.0.0';
              return newVersion !== currentVersion;
            }
            const current = p.version || p.Version;
            const latest = p.latest_version || p.latestVersion;
            return latest && current && latest !== current;
          });
          
          console.log('📊 Plugin Data State:', {
            hasData: !!pluginData,
            pluginsCount: plugins.length,
            summary,
            status: pluginData.status,
            filterCounts,
            updateCount: filterCounts.update_available,
            pluginsWithUpdatesCount: pluginsWithUpdates.length,
            samplePlugin: plugins[0],
            samplePluginUpdate: plugins[0]?.update,
            allPluginsWithUpdateField: plugins.filter(p => p.update || p.hasUpdate).map(p => ({
              name: p.name,
              version: p.version,
              hasUpdate: p.hasUpdate,
              update: p.update,
              latest_version: p.latest_version
            })),
            pluginsWithUpdates: pluginsWithUpdates.map(p => ({
              name: p.name,
              version: p.version,
              update: p.update,
              hasUpdate: p.hasUpdate,
              latest_version: p.latest_version
            }))
          });
          
          // Log detailed update information
          if (pluginsWithUpdates.length > 0) {
            console.log('✅ Plugins with updates detected:', pluginsWithUpdates.map(p => ({
              name: p.name,
              current: p.version,
              new: p.update?.new_version || p.latest_version,
              updateObject: p.update
            })));
          } else {
            console.warn('⚠️ No plugins with updates detected. Checking all plugins...');
            plugins.forEach((p, idx) => {
              if (idx < 5) { // Log first 5 plugins
                console.log(`Plugin ${idx + 1}:`, {
                  name: p.name,
                  version: p.version,
                  hasUpdate: p.hasUpdate,
                  update: p.update,
                  latest_version: p.latest_version,
                  hasUpdateField: !!p.update
                });
              }
            });
          }
        }
        if (error) {
          console.error('❌ Plugin Error State:', error);
        }
      }, [pluginData, plugins.length, summary, error, filterCounts]);

  if (loading) {
    return (
      <div className="loading" style={{ textAlign: 'center', padding: '40px' }}>
        {toast && <div className={`toast toast-${toast.type}`}>{toast.message}</div>}
        <div style={{ fontSize: '1.2em', color: '#666' }}>Loading plugins...</div>
        <div style={{ fontSize: '0.9em', color: '#999', marginTop: '10px' }}>
          Fetching from: {site?.url}
        </div>
      </div>
    );
  }
  
  return (
    <div className="website-updates-container">
      {toast && <div className={`toast toast-${toast.type}`}>{toast.message}</div>}
      {/* Header */}
      <div className="updates-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px', position: 'relative' }}>
            Plugins
            {filterCounts.update_available > 0 && (
              <span 
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  borderRadius: '50%',
                  minWidth: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '0 6px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  marginLeft: '5px'
                }}
              >
                {filterCounts.update_available}
              </span>
            )}
            {autoRefresh && <span className="live-indicator">● LIVE</span>}
          </h2>
          {pluginData && (
            <div className="plugin-summary" style={{ marginTop: '10px', color: '#666', fontSize: '0.9em' }}>
              {summary.total} items
              {lastUpdated && (
                <span style={{ marginLeft: '15px' }}>
                  Last checked: {new Date(lastUpdated).toLocaleString()}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="updates-actions" style={{ display: 'flex', alignItems: 'center' }}>
          <div className="refresh-controls" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.9em' }}>Auto-refresh</span>
            </label>
            {autoRefresh && (
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '0.9em'
                }}
              >
                <option value={10}>Every 10s</option>
                <option value={30}>Every 30s</option>
                <option value={60}>Every 1min</option>
                <option value={300}>Every 5min</option>
              </select>
            )}
            <button 
              onClick={handleRefresh} 
              disabled={isRefreshing}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isRefreshing ? 'not-allowed' : 'pointer',
                opacity: isRefreshing ? 0.6 : 1
              }}
            >
              {isRefreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
            </button>
            <button 
              onClick={() => setShowTestEndpoint(!showTestEndpoint)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔍 Test
            </button>
          </div>
        </div>
      </div>

      {/* Last update diagnostic panel */}
      {lastResult && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 14px',
            borderRadius: '6px',
            border: `1px solid ${lastResult.success ? '#c3e6cb' : '#f5c6cb'}`,
            backgroundColor: lastResult.success ? '#d4edda' : '#f8d7da',
            fontSize: '0.85em',
          }}
        >
          <strong style={{ display: 'block', marginBottom: '6px' }}>
            {lastResult.success ? 'Last update succeeded' : 'Last update failed'}
            {typeof lastResult.status === 'number' && ` (HTTP ${lastResult.status})`}
          </strong>
          <div style={{ marginBottom: '6px' }}>
            {lastResult.message || lastResult.error || 'No message returned from WordPress.'}
          </div>
          {lastResult.data && (
            <details>
              <summary style={{ cursor: 'pointer' }}>View raw WordPress response JSON</summary>
              <pre
                style={{
                  marginTop: '8px',
                  maxHeight: '260px',
                  overflow: 'auto',
                  background: '#fff',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6',
                }}
              >
                {JSON.stringify(lastResult.data, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && error.status === 'error' && (
        <div className="error-message" style={{ 
          padding: '20px', 
          backgroundColor: '#f8d7da', 
          border: '1px solid #f5c6cb', 
          borderRadius: '4px', 
          marginBottom: '20px',
          color: '#721c24'
        }}>
          <div style={{ marginBottom: '15px' }}>
            <strong style={{ fontSize: '1.1em' }}>⚠️ Error:</strong>
            <p style={{ margin: '10px 0', whiteSpace: 'pre-line' }}>
              {error.errorMessage || 'Failed to fetch plugins from WordPress. All endpoints tried failed.'}
            </p>
          </div>
          
              {error.suggestions && error.suggestions.length > 0 && (
                <div style={{ 
                  marginTop: '15px', 
                  padding: '15px', 
                  backgroundColor: '#fff3cd', 
                  borderRadius: '4px',
                  border: '1px solid #ffc107'
                }}>
                  <strong style={{ color: '#856404' }}>💡 How to Fix:</strong>
                  <ul style={{ margin: '10px 0', paddingLeft: '20px', color: '#856404' }}>
                    {error.suggestions.map((suggestion, idx) => (
                      <li key={idx} style={{ marginBottom: '8px' }}>{suggestion}</li>
                    ))}
                  </ul>
                  {(error.errorMessage && error.errorMessage.includes('unreachable')) || 
                   (error.suggestions && error.suggestions.some(s => s.includes('Install'))) ? (
                    <div style={{ 
                      marginTop: '15px', 
                      padding: '12px', 
                      backgroundColor: '#e7f3ff', 
                      borderRadius: '4px',
                      border: '1px solid #2271b1'
                    }}>
                      <strong style={{ color: '#2271b1' }}>📖 Installation Guide:</strong>
                      <p style={{ color: '#2271b1', margin: '8px 0', fontSize: '0.95em' }}>
                        Need step-by-step instructions? Check the installation guide:
                      </p>
                      <ol style={{ color: '#2271b1', margin: '8px 0', paddingLeft: '25px', fontSize: '0.9em' }}>
                        <li>Install <code>wordpress-plugin-webmonitor.php</code> in WordPress (Plugins → Add New → Upload Plugin)</li>
                        <li>Activate the plugin</li>
                        <li>Create Application Password (Users → Profile → Application Passwords)</li>
                        <li>Enter credentials in WebMonitor (Sites → Edit Site)</li>
                      </ol>
                      <p style={{ color: '#2271b1', margin: '8px 0', fontSize: '0.9em', fontStyle: 'italic' }}>
                        See <code>WORDPRESS_PLUGIN_INSTALLATION_GUIDE.md</code> for detailed instructions.
                      </p>
                    </div>
                  ) : null}
                </div>
              )}
        </div>
      )}

      {/* Test Endpoint Panel */}
      {showTestEndpoint && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #dee2e6', 
          borderRadius: '4px', 
          marginBottom: '20px'
        }}>
          <h3 style={{ marginTop: 0 }}>Test Endpoint</h3>
          <button
            onClick={handleTestEndpoint}
            disabled={testingEndpoint}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: testingEndpoint ? 'not-allowed' : 'pointer',
              opacity: testingEndpoint ? 0.6 : 1
            }}
          >
            {testingEndpoint ? 'Testing...' : 'Test Health Endpoint'}
          </button>
          
          {testResult && (
            <div style={{ 
              marginTop: '15px', 
              padding: '15px', 
              backgroundColor: testResult.summary?.healthOk ? '#d4edda' : '#f8d7da',
              borderRadius: '4px',
              border: `1px solid ${testResult.summary?.healthOk ? '#c3e6cb' : '#f5c6cb'}`
            }}>
              {testResult.summary ? (
                <>
                  <strong>Test Results:</strong>
                  <p>Health Endpoint: {testResult.summary.healthOk ? '✅ Working' : '❌ Failed'}</p>
                  <p>Plugins Test Endpoint: {testResult.summary.pluginsTestOk ? '✅ Working' : '❌ Failed'}</p>
                  {testResult.summary.pluginsFound > 0 && (
                    <p style={{ color: '#155724', fontWeight: 600, marginTop: '10px' }}>
                      ✅ Found {testResult.summary.pluginsFound} plugin(s)! Try refreshing plugins now.
                    </p>
                  )}
                  {testResult.summary.pluginsTestOk && testResult.summary.pluginsFound === 0 && (
                    <p style={{ color: '#856404', marginTop: '10px' }}>
                      ⚠️ Endpoint works but no plugins found. This might be normal if WordPress has no plugins installed.
                    </p>
                  )}
                  {!testResult.summary.healthOk && (
                    <p style={{ color: '#721c24', marginTop: '10px' }}>
                      ❌ Health endpoint failed. The WebMonitor plugin may not be installed. Install the plugin first.
                    </p>
                  )}
                  {testResult.attempts && testResult.attempts.length > 0 && (
                    <div style={{ marginTop: '15px' }}>
                      <p style={{ fontWeight: 600 }}>Attempts:</p>
                      <ul style={{ paddingLeft: '20px', color: '#555', fontSize: '0.9em' }}>
                        {testResult.attempts.map((attempt, index) => (
                          <li key={`${attempt.endpoint}-${index}`}>
                            <strong>{attempt.type}</strong> → {attempt.endpoint}
                            {attempt.success
                              ? ' ✅'
                              : ` ❌ (${attempt.status || 'N/A'}) ${attempt.error || ''}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <strong>{testResult.success ? '✅ Success' : '❌ Failed'}</strong>
                  {testResult.status && <p>Status: {testResult.status}</p>}
                  {testResult.error && <p>Error: {testResult.error}</p>}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters and Search */}
      {plugins.length > 0 && (
        <div style={{ 
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#fff',
          borderRadius: '4px',
          border: '1px solid #ddd'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            {/* Filter Links */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setFilter('all'); }}
                style={{
                  textDecoration: 'none',
                  color: filter === 'all' ? '#2271b1' : '#0073aa',
                  fontWeight: filter === 'all' ? 600 : 'normal',
                  borderBottom: filter === 'all' ? '2px solid #2271b1' : 'none',
                  paddingBottom: '5px'
                }}
              >
                All ({filterCounts.all})
              </a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setFilter('active'); }}
                style={{
                  textDecoration: 'none',
                  color: filter === 'active' ? '#2271b1' : '#0073aa',
                  fontWeight: filter === 'active' ? 600 : 'normal',
                  borderBottom: filter === 'active' ? '2px solid #2271b1' : 'none',
                  paddingBottom: '5px'
                }}
              >
                Active ({filterCounts.active})
              </a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setFilter('inactive'); }}
                style={{
                  textDecoration: 'none',
                  color: filter === 'inactive' ? '#2271b1' : '#0073aa',
                  fontWeight: filter === 'inactive' ? 600 : 'normal',
                  borderBottom: filter === 'inactive' ? '2px solid #2271b1' : 'none',
                  paddingBottom: '5px'
                }}
              >
                Inactive ({filterCounts.inactive})
              </a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setFilter('update_available'); }}
                style={{
                  textDecoration: 'none',
                  color: filter === 'update_available' ? '#2271b1' : '#0073aa',
                  fontWeight: filter === 'update_available' ? 600 : 'normal',
                  borderBottom: filter === 'update_available' ? '2px solid #2271b1' : 'none',
                  paddingBottom: '5px'
                }}
              >
                Update Available ({filterCounts.update_available})
              </a>
            </div>

            {/* Search */}
            <input
              type="text"
              placeholder="Search installed plugins..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.9em',
                minWidth: '200px'
              }}
            />
          </div>

          {/* Bulk Actions */}
          {selectedPlugins.length > 0 && (
            <div style={{ 
              marginTop: '15px', 
              padding: '10px',
              backgroundColor: '#f0f0f1',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Bulk actions</option>
                <option value="activate">Activate</option>
                <option value="deactivate">Deactivate</option>
                <option value="update">Update</option>
              </select>
              <button
                onClick={() => {
                  alert(`Bulk action "${bulkAction}" would be applied to ${selectedPlugins.length} plugin(s)`);
                  setSelectedPlugins([]);
                  setBulkAction('');
                }}
                disabled={!bulkAction}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#2271b1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: bulkAction ? 'pointer' : 'not-allowed',
                  opacity: bulkAction ? 1 : 0.6
                }}
              >
                Apply
              </button>
              <span style={{ color: '#666', fontSize: '0.9em' }}>
                {selectedPlugins.length} plugin(s) selected
              </span>
            </div>
          )}
        </div>
      )}

      {/* Plugin List Table */}
      {filteredPlugins.length > 0 ? (
        <div className="plugins-table-container" style={{ marginTop: '20px' }}>
          <table className="plugins-table" style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            backgroundColor: '#fff',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', width: '30px' }}>
                  <input
                    type="checkbox"
                    checked={selectedPlugins.length === filteredPlugins.length && filteredPlugins.length > 0}
                    onChange={handleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Plugin</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Created Date</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Update Available</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Update Completed</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Updated By</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Auto-update</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlugins.map((plugin, index) => {
                const pluginSlug = plugin.slug || plugin.plugin || index;
                const isSelected = selectedPlugins.includes(pluginSlug);
                const slugValue = getPluginSlug(plugin);
                
                // REAL WORDPRESS UPDATES ONLY:
                // Use hasWordPressReportedUpdate() so we only show updates when
                // WordPress core actually reports update.new_version metadata.
                const updateAvailable = hasWordPressReportedUpdate(plugin);
                
                // Format timestamps
                const createdDate = formatCreatedDate(plugin.createdDate || plugin.installed_at);
                const updateAvailableDate = formatCreatedDate(plugin.updateAvailableDate);
                const updateCompletedDate = formatCreatedDate(plugin.updateCompletedDate);
                
                // Debug log to verify update status for ALL plugins
                console.debug(
                  `[Plugin Update Check] slug=${slugValue || plugin.slug || plugin.plugin} ` +
                  `local_version=${plugin.version || plugin.Version} ` +
                  `new_version=${plugin.update?.new_version || plugin.update?.newVersion || 'n/a'} ` +
                  `hasUpdateFlag=${plugin.hasUpdate === true} ` +
                  `updateAvailable=${updateAvailable}`
                );
                
                return (
                  <tr 
                    key={pluginSlug}
                    style={{ 
                      borderBottom: '1px solid #e9ecef',
                      backgroundColor: isSelected ? '#f0f6fc' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => !isSelected && (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                    onMouseLeave={(e) => !isSelected && (e.currentTarget.style.backgroundColor = '#fff')}
                  >
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectPlugin(pluginSlug)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong style={{ fontSize: '1em', color: '#23282d' }}>{plugin.name}</strong>
                      </div>
                      <div style={{ marginTop: '5px', fontSize: '0.85em', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        {(() => {
                          const slugValue = plugin.slug || plugin.plugin || index;
                          const pluginFile = plugin.plugin || plugin.plugin_file;
                          const pluginKey = slugValue || pluginFile || 'unknown';
                          const isActivating = activatingPlugins.has(pluginKey);
                          const isDeactivating = deactivatingPlugins.has(pluginKey);
                          const isActive = plugin.active === true || plugin.active === 'active' || plugin.status === 'active';
                          
                          if (isActive) {
                            return (
                              <a 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  deactivatePlugin(slugValue, pluginFile, plugin.name || plugin.Name);
                                }} 
                                style={{ 
                                  color: '#a00', 
                                  textDecoration: 'none', 
                                  marginRight: '10px', 
                                  cursor: isDeactivating ? 'wait' : 'pointer',
                                  opacity: isDeactivating ? 0.7 : 1,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                disabled={isDeactivating}
                              >
                                {isDeactivating && <span className="button-spinner" aria-hidden="true" />}
                                {isDeactivating ? 'Deactivating…' : 'Deactivate'}
                              </a>
                            );
                          } else {
                            return (
                              <a 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  activatePlugin(slugValue, pluginFile, plugin.name || plugin.Name);
                                }} 
                                style={{ 
                                  color: '#2271b1', 
                                  textDecoration: 'none', 
                                  marginRight: '10px', 
                                  cursor: isActivating ? 'wait' : 'pointer',
                                  opacity: isActivating ? 0.7 : 1,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                disabled={isActivating}
                              >
                                {isActivating && <span className="button-spinner" aria-hidden="true" />}
                                {isActivating ? 'Activating…' : 'Activate'}
                              </a>
                            );
                          }
                        })()}
                        {plugin.plugin_uri && (
                          <a 
                            href={plugin.plugin_uri} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#2271b1', textDecoration: 'none', marginRight: '10px' }}
                          >
                            View details
                          </a>
                        )}
                        {updateAvailable && slugValue && (
                          <a
                            href="#"
                            onClick={(e) => {
                              if (e && e.preventDefault) e.preventDefault();
                              if (e && e.stopPropagation) e.stopPropagation();

                              if (isUpdatingTarget(slugValue)) {
                                console.log('[Plugins] Update already in progress for:', slugValue);
                                return;
                              }

                              // Get package URL from multiple possible sources
                              let packageUrl = plugin.update?.package || 
                                             plugin.update?.download_link || 
                                             plugin.update?.package_url;
                              
                              // If no package URL from WordPress, construct WordPress.org URL
                              if (!packageUrl && slugValue) {
                                // Extract base slug (remove any path components)
                                const baseSlug = slugValue.split('/')[0];
                                packageUrl = `https://downloads.wordpress.org/plugin/${baseSlug}.zip`;
                                console.log('[Plugins] Constructed package URL:', packageUrl);
                              }
                              
                              const pluginFile = plugin.plugin || plugin.plugin_file;
                              const pluginName = plugin.name || plugin.Name || slugValue || 'Unknown Plugin';
                              
                              // Show reason modal first
                              setReasonModal({
                                isOpen: true,
                                action: 'update',
                                pluginSlug: slugValue,
                                pluginFile,
                                pluginName,
                                packageUrl,
                              });
                            }}
                            style={{
                              color: '#a00',
                              textDecoration: 'none',
                              marginRight: '10px',
                              cursor: isUpdatingTarget(slugValue) ? 'wait' : 'pointer',
                              opacity: isUpdatingTarget(slugValue) ? 0.7 : 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            {isUpdatingTarget(slugValue) && (
                              <span className="button-spinner" aria-hidden="true" />
                            )}
                            {isUpdatingTarget(slugValue) ? 'Updating…' : 'Update'}
                          </a>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px', color: '#666', fontSize: '0.9em', lineHeight: '1.6' }}>
                      <div style={{ marginBottom: '8px' }}>
                        {plugin.description || 'No description available.'}
                      </div>
                      <div style={{ color: '#999', fontSize: '0.85em' }}>
                        Version {plugin.version}
                        {plugin.author && (
                          <>
                            {' | By '}
                            {plugin.author_uri ? (
                              <a href={plugin.author_uri} target="_blank" rel="noopener noreferrer" style={{ color: '#2271b1', textDecoration: 'none' }}>
                                {plugin.author}
                              </a>
                            ) : (
                              plugin.author
                            )}
                          </>
                        )}
                        {(plugin.isUpdateAvailable === true || plugin.hasUpdate === true) && (
                          <span style={{ 
                            color: '#d63638', 
                            fontWeight: 600, 
                            marginLeft: '10px',
                            backgroundColor: '#fff3cd',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            display: 'inline-block'
                          }}>
                            ⚠️ Update Available: {plugin.update?.new_version || plugin.latest_version || plugin.available_version || 'New version available'}
                          </span>
                        )}
                        <div style={{ marginTop: '6px', color: '#555', fontSize: '0.8em' }}>
                          <strong>Last Updated:</strong> {resolveLastUpdated(plugin)}{' '}
                          | <strong>Compatible up to:</strong> {resolveCompatible(plugin)}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '0.9em', color: '#666' }}>
                      {createdDate ? (
                        <div>
                          <div>{createdDate.date}</div>
                          <div style={{ color: '#999', fontSize: '0.85em' }}>{createdDate.time}</div>
                        </div>
                      ) : (
                        <span style={{ color: '#999' }}>N/A</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', fontSize: '0.9em', color: '#666' }}>
                      {updateAvailableDate ? (
                        <div>
                          <div>{updateAvailableDate.date}</div>
                          <div style={{ color: '#999', fontSize: '0.85em' }}>{updateAvailableDate.time}</div>
                        </div>
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', fontSize: '0.9em', color: '#666' }}>
                      {updateCompletedDate ? (
                        <div>
                          <div>{updateCompletedDate.date}</div>
                          <div style={{ color: '#999', fontSize: '0.85em' }}>{updateCompletedDate.time}</div>
                        </div>
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', fontSize: '0.9em', color: '#666' }}>
                      {plugin.updatedBy ? (
                        <span>{plugin.updatedBy}</span>
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', fontSize: '0.9em' }}>
                      {(() => {
                        const slugValue = getPluginSlug(plugin);
                        if (!slugValue) return <span style={{ color: '#999' }}>-</span>;
                        
                        // Normalize slug to match how it's stored in autoUpdateSettings
                        const normalizedSlug = normalizeSlug(slugValue);
                        const isAutoUpdateEnabled = normalizedSlug ? (autoUpdateSettings[normalizedSlug] === true) : false;
                        const isUpdating = normalizedSlug ? updatingAutoUpdate.has(normalizedSlug) : false;
                        
                        return (
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            cursor: isUpdating ? 'wait' : 'pointer',
                            opacity: isUpdating ? 0.6 : 1
                          }}>
                            <input
                              type="checkbox"
                              checked={isAutoUpdateEnabled}
                              onChange={() => toggleAutoUpdate(slugValue, plugin.name || plugin.Name)}
                              disabled={isUpdating}
                              style={{ cursor: isUpdating ? 'wait' : 'pointer' }}
                            />
                            <span style={{ fontSize: '0.85em', color: isAutoUpdateEnabled ? '#2271b1' : '#666' }}>
                              {isUpdating ? 'Updating...' : (isAutoUpdateEnabled ? 'Enabled' : 'Enable')}
                            </span>
                            {isAutoUpdateEnabled && (
                              <span 
                                style={{ 
                                  fontSize: '0.75em', 
                                  color: '#28a745',
                                  fontWeight: 600,
                                  marginLeft: '4px'
                                }}
                                title="Auto-update is enabled. Plugin will be automatically updated when updates are available."
                              >
                                ✓
                              </span>
                            )}
                          </label>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : plugins.length > 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginTop: '20px'
        }}>
          <p style={{ fontSize: '1.1em', color: '#666' }}>
            No plugins match your search criteria.
          </p>
        </div>
      ) : !error && plugins.length === 0 && (
        <div className="no-plugins" style={{ 
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginTop: '20px'
        }}>
          <p style={{ fontSize: '1.1em', color: '#666', marginBottom: '10px' }}>
            {pluginData?.status === 'ok' 
              ? '✅ Connection successful, but no plugins found on this WordPress site.'
              : 'No plugins found or unable to fetch plugin data.'}
          </p>
          {pluginData?.status === 'ok' ? (
            <p style={{ color: '#856404', fontSize: '0.95em' }}>
              This WordPress site may not have any plugins installed, or all plugins are hidden from the REST API.
            </p>
          ) : (
            <>
              <p style={{ color: '#999' }}>This could be due to:</p>
              <ul style={{ 
                textAlign: 'left', 
                maxWidth: '500px', 
                margin: '15px auto',
                color: '#666'
              }}>
                <li>WordPress credentials not configured</li>
                <li>WebMonitor plugin not installed on WordPress site</li>
                <li>WordPress REST API not accessible</li>
                <li>SSL certificate issues (for staging sites)</li>
                <li>Firewall or network restrictions</li>
              </ul>
              <button
                onClick={() => setShowTestEndpoint(true)}
                style={{
                  marginTop: '15px',
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Test Endpoint Connection
              </button>
            </>
          )}
        </div>
      )}

      {/* Reason Modal */}
      <ReasonModal
        isOpen={reasonModal.isOpen}
        onClose={handleReasonClose}
        onConfirm={handleReasonConfirm}
        action={reasonModal.action}
        pluginName={reasonModal.pluginName}
      />

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '12px 20px',
            backgroundColor: toast.type === 'error' ? '#dc3545' : toast.type === 'success' ? '#28a745' : '#007bff',
            color: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            zIndex: 9999,
            animation: 'fadeIn 0.3s ease-in'
          }}
        >
          {toast.message}
        </div>
      )}

    </div>
  );
};

export default Plugins;
